import { createTheme, ThemeOptions } from '@mui/material/styles';
import { colors } from './colors';
import SortTableIcon from 'components/v2/icons/SortTableIcon';

export const fontUri =
  'https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap';

declare module '@mui/material/styles' {
  interface Palette {
    link: Palette['primary'];
    borderColor: Palette['primary'];
  }
  interface PaletteOptions {
    link: PaletteOptions['primary'];
    borderColor: PaletteOptions['primary'];
  }
}

export const muiTheme: ThemeOptions = createTheme({
  palette: {
    primary: {
      main: '#0052CC',
      light: '#4C9AFF',
      dark: '#0747A6',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: colors.base.level70,
      secondary: colors.base.level60,
      disabled: colors.base.level50,
    },
    grey: {
      50: colors.gray[50],
      100: colors.gray[100],
      200: colors.gray[200],
      600: colors.gray[600],
      900: colors.gray[900],
    },
    link: {
      main: colors.link,
    },
    borderColor: {
      main: colors.gray[300],
    },
    success: {
      light: colors.emerald.level10,
      main: colors.emerald.level50,
      dark: colors.emerald.level60,
      contrastText: '#ffffff',
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          minWidth: 'fit-content',
          textTransform: 'unset',
        },
        loadingIndicator: {
          color: colors.base.white,
        },
        contained: {
          background: colors.specific.buttonBg,
          color: colors.base.white,
        },
        outlined: {
          boxShadow: '0px 1px 2px 0px rgba(10, 13, 20, 0.03)',
          border: `1px solid ${colors.gray[300]}`,
          color: colors.mono.level7_900,
        },
        sizeSmall: {
          padding: '0rem 0.5rem',
          gap: '0.25rem',
          borderRadius: '0.5rem',
          height: '2rem',
          fontSize: '1rem',
          fontWeight: 500,
        },
        sizeMedium: {
          padding: '0rem 0.75rem',
          gap: '0.5rem',
          borderRadius: '0.5rem',
          height: '2.5rem',
          fontSize: '1rem',
          fontWeight: 500,
        },
        sizeLarge: {
          padding: '0rem 0.75rem',
          gap: '0.5rem',
          borderRadius: '0.5rem',
          height: '3.5rem',
          fontSize: '1rem',
          fontWeight: 500,
        },
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            '& .MuiButton-loadingIndicator': {
              color: colors.red[500],
            },
          },
        },
        {
          props: { variant: 'contained' },
          style: {
            '& .MuiButton-loadingIndicator': {
              color: colors.base.white,
            },
          },
        },
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: '0.5rem',
          background: colors.base.white,
          borderColor: `${colors.base.level40}`,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: `1.5px solid ${theme.palette.primary.main}`,
            // border: `1.5px solid ${colors.red[500]}`,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            // borderColor: `1.5px solid ${colors.red[500]}`,
          },
        }),
      },
      variants: [
        {
          props: { size: 'small' },
          style: {
            display: 'flex',
            height: '2.5rem', // 40px
            alignItems: 'center',
            alignSelf: 'stretch',
          },
        },
        {
          props: { size: 'medium' },
          style: {
            display: 'flex',
            height: '3.5rem', // 56px
            alignItems: 'center',
            alignSelf: 'stretch',
          },
        },
      ],
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '0.5rem',
          border: `1px solid ${colors.gray[300]}`,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {},
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          borderRadius: '0.5rem',
          background: colors.gray[100],
          padding: '0.125rem 0.75rem',
          height: '3.5rem',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          background: 'none',
        },
        body: ({ theme }) => ({
          ...theme.typography.body1,
          color: colors.gray[600],
          height: '3rem',
        }),
        head: ({ theme }) => ({
          ...theme.typography.body1,
          color: colors.gray[600],
          padding: '0.125rem 0.75rem',
          height: '2.5rem',
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.gray[300]}`,
        },
        head: {
          borderBottom: 'none !important',
        },
      },
    },
    // MuiTooltip: { // removed cause its not good UI when following figma design
    //   styleOverrides: {
    //     tooltip: {
    //       display: 'flex',
    //       padding: '0.5rem',
    //       justifyContent: 'center',
    //       alignItems: 'center',
    //       gap: '0.625rem',
    //       borderRadius: '0.5rem',
    //       background: colors.gray[900],
    //       fontSize: '0.9375rem',
    //       fontWeight: 500,
    //     },
    //   },
    // },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"Be Vietnam Pro", sans-serif',
          fontSize: '1rem',
          fontWeight: 500,
          textTransform: 'capitalize',
          lineHeight: '1.2rem',
          letterSpacing: '-0.02rem',
          paddingLeft: 0,
          color: colors.gray[500],
          '&.Mui-selected': {
            color: colors.gray[900],
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.gray[300]}`,
        },
        indicator: {
          backgroundColor: colors.red[500],
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          borderColor: `${colors.base.level40}`,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: '2.125rem',
          height: '1.25rem',
          padding: 0,
        },
        switchBase: {
          padding: 0,
          margin: 2,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: colors.emerald.level50,
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5,
            },
          },

          '&.Mui-disabled .MuiSwitch-thumb': {
            color: 'grey',
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.7,
          },
        },
        thumb: {
          boxSizing: 'border-box',
          width: '1rem',
          height: '1rem',
        },
        track: {
          borderRadius: '1rem',
          backgroundColor: colors.gray[300],
          opacity: 1,
          transition: 'background-color 500ms',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          padding: '1rem',
          border: `1px solid ${colors.gray[300]}`,
          borderRadius: `1rem`,
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          minHeight: 'fit-content',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiCardMedia: {
      styleOverrides: {
        root: {
          borderRadius: '0.5rem',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '0.75rem',
          padding: '0.25rem',
          backgroundColor: colors.base.level30,
          display: 'flex',
        },
        grouped: {
          flex: 1,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: colors.mono.level7_900,
          fontSize: '0.875rem',
          textTransform: 'capitalize',
          borderRadius: '0.5rem !important',
          border: 'none',
          fontWeight: 500,
          height: '2rem',
          '&.Mui-selected': {
            backgroundColor: 'white',
            border: `1px solid ${colors.base.level40}`,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: colors.red[500],
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: colors.ruby.level50,
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          PaperProps: {
            sx: {
              maxHeight: 200,
            },
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.Mui-active': {
            color: colors.pink[500],
          },
          '&.Mui-completed': {
            color: colors.pink[500],
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '0.5rem',
          border: `1px solid ${colors.base.level40}`,
          boxShadow: 'none',
          background: 'white',
          transition: 'all 0.3s ease',
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '1rem',
        },
        expandIconWrapper: {
          color: colors.primary.main,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {},
      },
    },
    MuiTableSortLabel: {
      defaultProps: {
        IconComponent: SortTableIcon,
      },
      styleOverrides: {
        icon: ({ ownerState }) => ({
          opacity: 1,
          transform: 'none',
          '& > path:nth-of-type(1)': {
            fill: ownerState.active && ownerState.direction === 'asc' ? colors.primary.main : 'initial',
          },
          '& > path:nth-of-type(2)': {
            fill: ownerState.active && ownerState.direction === 'desc' ? colors.primary.main : 'initial',
          },
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primary.light,
        },
        bar: {
          backgroundColor: colors.primary.main,
        },
        barColorPrimary: {
          backgroundColor: colors.primary.main,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 4,
        },
        rail: {
          opacity: 1,
          backgroundColor: '#f2f2f2',
        },
        track: {
          border: 'none',
          background: colors.primary.main,
        },
        thumb: {
          height: 16,
          width: 16,
          backgroundColor: 'white',
          border: `1px solid #f2f2f2`,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          '&.Mui-focusVisible': {
            boxShadow: 'none',
          },
          '&.Mui-active': {
            boxShadow: 'none',
          },
        },
      },
    },
  },
  typography: {
    fontFamily: '"Be Vietnam Pro", sans-serif',
    htmlFontSize: 16,
    h1: {
      color: colors.gray[900],
      fontSize: '4rem',
      fontWeight: 500,
      lineHeight: '1.2rem',
      letterSpacing: '-0.08rem',
    },
    h2: {
      color: colors.gray[900],
      fontSize: '3rem',
      fontWeight: 500,
      lineHeight: '1.2rem',
      letterSpacing: '-0.06rem',
    },
    h3: {
      color: colors.gray[900],
      fontSize: '2rem',
      fontWeight: 500,
      lineHeight: '1.2rem',
      letterSpacing: '-0.04rem',
    },
    h4: {
      color: colors.gray[900],
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: '1.2rem',
      letterSpacing: '-0.03rem',
    },
    h5: {
      color: colors.gray[900],
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: '1.2rem',
      letterSpacing: '-0.02rem',
    },
    h6: {
      color: colors.gray[900],
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: '1.2rem',
      letterSpacing: '-0.0175rem',
    },
    body1: {
      color: colors.gray[900],
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.5rem',
      letterSpacing: '-0.02rem',
    },
    body2: {
      color: colors.gray[900],
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: '1.2rem',
      letterSpacing: '-0.0175rem',
    },
  },
});
